import { Zap } from 'lucide-react'
import { FC, useCallback } from 'react'
import { Link, useNavigate } from 'react-router'
import r from 'routes'

import { App } from '@/apps'
import AppIcon from '@/components/app-icon'
import LoadingButton from '@/components/loading-button'
import OnboardingDialog from '@/components/onboarding-dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { idleTimer } from '@/hooks/useActionTimer'
import { useAPI } from '@/hooks/useAPI'
import useAvailableApps from '@/hooks/useAvailableApps'
import { cn } from '@/lib/utils'
import { useAuthSelector, useBillingSelector } from '@/store'
import { startFreeTrial } from '@/thunks/billing/free-trial'

const descriptions = {
  'document-signature': 'Request customers to sign documents.',
  'e-form': 'Collect sensitive data without exposing it to agents.',
  'gateway-payments': 'Accept payments in the contact center.',
  'identity-verification': "Verify customers' identities in real-time.",
  'one-time-passcode': 'Update customer authentication in a few hours.',
  passkeys: 'Authenticate customers with their smartphone.',
  payments: 'Accept digital payments in the contact center, digital and voice.',
} as const

const Home: FC = () => {
  const navigate = useNavigate()
  const auth = useAuthSelector()
  const [start, { timer }] = useAPI(startFreeTrial)
  const apps = useAvailableApps()

  const billingApps = useBillingSelector()

  const handleStartFreeTrial = useCallback(
    async (app: App) => {
      timer.setId(app)
      await start({ app })
      navigate(r.apps[app].root)
    },
    [start, timer, navigate]
  )

  return (
    <>
      {!auth.hasOnboarded && <OnboardingDialog />}
      <div className="px-4 py-12 mx-auto w-full md:max-w-7xl">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map(({ id, title, color, icon }) => (
            <Card
              key={id}
              className="flex relative flex-col transition-all duration-300 cursor-pointer group hover:shadow-lg"
              onClick={() => navigate(r.apps[id].root)}
              data-testid={`home-app-${id}-card`}
            >
              <div className="flex-grow p-6">
                <div className="flex justify-between items-start">
                  <div className="flex gap-6 items-start">
                    <AppIcon Icon={icon} color={color} />
                    <div>
                      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
                      <p className="text-sm leading-relaxed text-muted-foreground/80">{descriptions[id]}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 pt-0 bg-gradient-to-b from-transparent to-muted/10">
                {billingApps[id].status === 'inactive' ? (
                  <LoadingButton
                    timer={timer.id === id ? timer : idleTimer}
                    variant="outline"
                    className={cn(
                      'w-full h-10',
                      'backdrop-blur-sm bg-background/80',
                      'hover:bg-background',
                      'text-sm font-medium',
                      'transition-all duration-300'
                    )}
                    onClick={() => handleStartFreeTrial(id)}
                  >
                    <span className="flex gap-2 items-center">
                      <Zap className="w-4 h-4 text-primary" />
                      Start Free Trial
                    </span>
                  </LoadingButton>
                ) : (
                  <Button
                    variant="outline"
                    size={'lg'}
                    className={`w-full shadow-none transition-all duration-300 hover:${color} font-sm`}
                    data-testid={`launch-app-${id}-button`}
                  >
                    <Link to={r.apps[id].root}>Launch app</Link>
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  )
}

export default Home
